import { resolvePath, type Scope } from './render.js';
import { FORMATTERS } from './resolve.js';
import {
  isBindV3,
  type BlockNode,
  type DocumentV3,
  type StyleOverrides,
} from './document-v3.js';

/**
 * Pure resolution of a v3 document: binds resolved (with optional format),
 * repeats expanded at page and block level, children recursed. Keys are
 * stable and unique per rendered instance: repeat iterations append `:i`,
 * and children inherit their ancestors' suffix.
 */

export interface ResolvedBlock {
  key: string;
  blockId: string;
  type: string;
  props: Record<string, unknown>;
  style?: StyleOverrides | undefined;
  children: ResolvedBlock[];
}

export interface ResolvedPageV3 {
  key: string;
  pageId: string;
  background?: string | undefined;
  pad?: string | undefined;
  blocks: ResolvedBlock[];
}

export interface ResolvedDocumentV3 {
  pages: ResolvedPageV3[];
  warnings: string[];
}

function resolveValueV3(raw: unknown, scope: Scope): unknown {
  if (isBindV3(raw)) {
    const v = resolvePath(scope, raw.$bind);
    if (raw.format) {
      const fmt = FORMATTERS[raw.format];
      return fmt ? fmt(v) : v;
    }
    return v;
  }
  if (Array.isArray(raw)) return raw.map((item) => resolveValueV3(item, scope));
  if (raw !== null && typeof raw === 'object') {
    return Object.fromEntries(
      Object.entries(raw as Record<string, unknown>).map(([k, v]) => [k, resolveValueV3(v, scope)]),
    );
  }
  return raw;
}

function resolveOne(
  node: BlockNode,
  scope: Scope,
  suffix: string,
  warnings: string[],
): ResolvedBlock {
  // A malformed block (props absent) must degrade to empty props, never crash
  // the whole document render — an editor bug should not white-screen the page.
  if (node.props === undefined || node.props === null) {
    warnings.push(`block "${node.id}" (type "${node.type}") has no props`);
  }
  const props = Object.fromEntries(
    Object.entries(node.props ?? {}).map(([k, v]) => [k, resolveValueV3(v, scope)]),
  );
  return {
    key: `${node.id}${suffix}`,
    blockId: node.id,
    type: node.type,
    props,
    style: node.style,
    children: node.children ? resolveBlocks(node.children, scope, warnings, suffix) : [],
  };
}

export function resolveBlocks(
  nodes: BlockNode[],
  scope: Scope,
  warnings: string[],
  suffix = '',
): ResolvedBlock[] {
  const out: ResolvedBlock[] = [];
  for (const node of nodes) {
    if (node.repeat) {
      const items = resolvePath(scope, node.repeat.over);
      if (!Array.isArray(items)) {
        warnings.push(`block "${node.id}": repeat.over "${node.repeat.over}" is not an array`);
        continue;
      }
      items.forEach((item, i) => {
        const itemScope: Scope = { ...scope, [node.repeat!.as]: item, '@index': i };
        out.push(resolveOne(node, itemScope, `${suffix}:${i}`, warnings));
      });
    } else {
      out.push(resolveOne(node, scope, suffix, warnings));
    }
  }
  return out;
}

export function resolveDocumentV3(doc: DocumentV3): ResolvedDocumentV3 {
  const warnings: string[] = [];
  const pages: ResolvedPageV3[] = [];
  for (const page of doc.pages) {
    if (page.repeat) {
      const items = resolvePath(doc.data, page.repeat.over);
      if (!Array.isArray(items)) {
        warnings.push(`page "${page.id}": repeat.over "${page.repeat.over}" is not an array`);
        continue;
      }
      items.forEach((item, i) => {
        const scope: Scope = { ...doc.data, [page.repeat!.as]: item, '@index': i };
        pages.push({
          key: `${page.id}:${i}`,
          pageId: page.id,
          background: page.background,
          pad: page.pad,
          blocks: resolveBlocks(page.blocks, scope, warnings, `:${i}`),
        });
      });
    } else {
      pages.push({
        key: page.id,
        pageId: page.id,
        background: page.background,
        pad: page.pad,
        blocks: resolveBlocks(page.blocks, doc.data, warnings),
      });
    }
  }
  return { pages, warnings };
}
